import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import { startClubSupportChat } from "../../../utils/platformSupport";
import { buildClinicPortalUrl, formatClinicHost } from "../../../utils/portalHost";
import { trackPaymentSuccess } from "../../../analytics/metrikaGoals";
import { clinicStatusLabel, ownerLegalAccepted, ownerLegalLabel } from "./platformClubsUtils";
import { formatRub, MetricCard } from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import {
  GRANDFATHER_PLAN_CODES,
  planIdsAll,
  planIdsByCodes,
  planIdsPublic,
} from "./billingUi";
import s from "./platformAdmin.module.css";

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clinic, setClinic] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState({
    saas_plan_id: "",
    status: "",
    crm_monthly_price_rub: "",
    crm_price_locked: false,
    billing_catalog: "current",
    billing_catalog_until: "",
    offer_plan_ids: [],
    crm_billing_period: "monthly",
    crm_paid_until: "",
  });
  const [modulesForm, setModulesForm] = useState({
    analytics_pro_paid_until: "",
    messengers_paid_until: "",
    member_social_paid_until: "",
    white_label_paid_until: "",
    bitrix24_paid_until: "",
    website_purchased: false,
  });
  const [checkout, setCheckout] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [writeBusy, setWriteBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const load = () => {
    setUsersLoading(true);
    Promise.all([
      platformAPI.getClinic(id),
      platformAPI.getPlans(),
      platformAPI.listClinicUsers(id),
      platformAPI.getFeaturesCatalog().catch(() => ({ features: [] })),
    ])
      .then(([detail, plansRes, usersRes, featRes]) => {
        const c = detail.clinic;
        setClinic(c);
        setEntitlements(detail.entitlements);
        setFeatureCatalog(featRes.features || []);
        const activePlans = plansRes.plans?.filter((p) => p.is_active) || [];
        setPlans(activePlans);
        setUsers(usersRes.users || []);
        const storedOffers = Array.isArray(c.offer_plan_ids)
          ? c.offer_plan_ids.map(String)
          : [];
        const isLegacy =
          c.billing_catalog === "legacy" ||
          c.billing?.billing_catalog_stored === "legacy";
        const fallbackOffers = isLegacy
          ? planIdsByCodes(activePlans, GRANDFATHER_PLAN_CODES)
          : planIdsPublic(activePlans);
        setBilling({
          saas_plan_id: c.saas_plan_id || "",
          status:
            c.billing?.is_paid && !c.billing?.is_trial
              ? "active"
              : c.billing?.is_demo_expired
                ? "trial"
                : c.billing?.status || c.status,
          crm_monthly_price_rub: c.crm_monthly_price_rub ?? "",
          crm_price_locked: Boolean(c.crm_price_locked),
          billing_catalog: isLegacy ? "legacy" : "current",
          billing_catalog_until: c.billing_catalog_until
            ? String(c.billing_catalog_until).slice(0, 10)
            : "",
          offer_plan_ids: storedOffers.length ? storedOffers : fallbackOffers,
          crm_billing_period: c.crm_billing_period || "monthly",
          crm_paid_until: c.billing?.paid_until
            ? String(c.billing.paid_until).slice(0, 10)
            : c.crm_paid_until
              ? String(c.crm_paid_until).slice(0, 10)
              : "",
        });
        setModulesForm({
          analytics_pro_paid_until: c.analytics_pro_paid_until
            ? String(c.analytics_pro_paid_until).slice(0, 10)
            : "",
          messengers_paid_until: c.messengers_paid_until
            ? String(c.messengers_paid_until).slice(0, 10)
            : "",
          member_social_paid_until: c.member_social_paid_until
            ? String(c.member_social_paid_until).slice(0, 10)
            : "",
          white_label_paid_until: c.white_label_paid_until
            ? String(c.white_label_paid_until).slice(0, 10)
            : "",
          bitrix24_paid_until: c.bitrix24_paid_until
            ? String(c.bitrix24_paid_until).slice(0, 10)
            : "",
          website_purchased: Boolean(c.website_purchased_at),
        });
      })
      .catch((e) => setErr(e.response?.data?.error || "Клуб не найден"))
      .finally(() => setUsersLoading(false));
  };

  useEffect(() => {
    load();
  }, [id]);

  useEffect(() => {
    if (searchParams.get("payment") === "success") {
      trackPaymentSuccess({ source: "platform_admin" });
      setMsg("Оплата принята. Синхронизируем подписку…");
      searchParams.delete("payment");
      setSearchParams(searchParams, { replace: true });
      load();
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!id || !billing.saas_plan_id) {
      setCheckout(null);
      return;
    }
    let cancelled = false;
    setCheckoutLoading(true);
    platformAPI
      .getClinicSubscriptionCheckout(id, {
        saas_plan_id: billing.saas_plan_id,
        crm_billing_period: billing.crm_billing_period,
      })
      .then((res) => {
        if (!cancelled) setCheckout(res.checkout || null);
      })
      .catch(() => {
        if (!cancelled) setCheckout(null);
      })
      .finally(() => {
        if (!cancelled) setCheckoutLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, billing.saas_plan_id, billing.crm_billing_period]);

  const featureNameByKey = useMemo(() => {
    const map = {};
    for (const f of featureCatalog) map[f.key] = f.name;
    return map;
  }, [featureCatalog]);

  const saveBilling = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      const offerIds = [...(billing.offer_plan_ids || [])].map(String);
      if (
        billing.saas_plan_id &&
        !offerIds.includes(String(billing.saas_plan_id))
      ) {
        offerIds.unshift(String(billing.saas_plan_id));
      }
      await platformAPI.updateClinicBilling(id, {
        saas_plan_id: billing.saas_plan_id || null,
        status: billing.status,
        crm_monthly_price_rub:
          billing.crm_monthly_price_rub === ""
            ? null
            : Number(billing.crm_monthly_price_rub),
        crm_price_locked: Boolean(billing.crm_price_locked),
        billing_catalog: "current",
        billing_catalog_until: billing.billing_catalog_until || null,
        offer_plan_ids: offerIds,
        crm_billing_period: billing.crm_billing_period,
        crm_paid_until: billing.crm_paid_until || null,
      });
      setMsg("Подписка сохранена");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения");
    }
  };

  const saveModules = async (e) => {
    e.preventDefault();
    setErr("");
    setMsg("");
    try {
      await platformAPI.updateClinicModules(id, {
        analytics_pro_paid_until: modulesForm.analytics_pro_paid_until || null,
        messengers_paid_until: modulesForm.messengers_paid_until || null,
        member_social_paid_until: modulesForm.member_social_paid_until || null,
        white_label_paid_until: modulesForm.white_label_paid_until || null,
        bitrix24_paid_until: modulesForm.bitrix24_paid_until || null,
        website_purchased: Boolean(modulesForm.website_purchased),
        clear_website: !modulesForm.website_purchased,
      });
      setMsg("Add-ons обновлены");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.error || "Ошибка сохранения модулей");
    }
  };

  const payOnline = async () => {
    if (!billing.saas_plan_id) {
      setErr("Выберите SaaS-тариф для оплаты");
      return;
    }
    setErr("");
    setMsg("");
    setPayBusy(true);
    try {
      const data = await platformAPI.initClinicSubscriptionPayment(id, {
        saas_plan_id: billing.saas_plan_id,
        crm_billing_period: billing.crm_billing_period,
      });
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        setErr("Ссылка на оплату не получена");
      }
    } catch (e2) {
      setErr(e2.response?.data?.error || "Онлайн-оплата недоступна");
    } finally {
      setPayBusy(false);
    }
  };

  const openSupportChat = async (userId) => {
    if (writeBusy) return;
    setErr("");
    setMsg("");
    setWriteBusy(true);
    try {
      const started = await startClubSupportChat(id, { userId });
      navigate(started.path);
    } catch (e2) {
      setErr(e2.response?.data?.error || "Не удалось открыть чат");
      setWriteBusy(false);
    }
  };

  const writeToClub = () => openSupportChat();

  const deleteClinic = async () => {
    if (deleteBusy) return;
    if (
      !window.confirm(
        "Удалить клуб? Клуб исчезнет из списка, доступ пользователей будет заблокирован, slug освободится. Данные останутся для статистики.",
      )
    ) {
      return;
    }
    setErr("");
    setMsg("");
    setDeleteBusy(true);
    try {
      await platformAPI.deleteClinic(id);
      navigate("/admin/tenants", {
        replace: true,
        state: { msg: "Клуб удалён из списка" },
      });
    } catch (e2) {
      setErr(e2.response?.data?.error || "Не удалось отключить клуб");
      setDeleteBusy(false);
    }
  };

  if (!clinic && !err) {
    return <div className={s.loading}>Загрузка клуба…</div>;
  }

  if (!clinic) {
    return (
      <div className={s.page}>
        <div className={s.alertError}>{err}</div>
        <Link to="/admin/tenants" className={s.heroActionBtnGhost}>
          ← К списку клубов
        </Link>
      </div>
    );
  }

  const features = entitlements?.features || {};
  const selectedPlan = plans.find((p) => p.id === billing.saas_plan_id);

  return (
    <div className={s.page}>
      <PlatformPageHeader
        backTo="/admin/tenants"
        backLabel="Клубы"
        title={clinic.name}
        lead={
          <>
            <a
              href={buildClinicPortalUrl(clinic.subdomain, "/manage")}
              target="_blank"
              rel="noopener noreferrer"
              className={s.inlineLink}
            >
              {formatClinicHost(clinic.subdomain)}
            </a>
            {" · "}
            {clinicStatusLabel(clinic)}
            {clinic.billing?.plan_label ? ` · ${clinic.billing.plan_label}` : ""}
            {clinic.billing?.paid_until
              ? ` · до ${String(clinic.billing.paid_until).slice(0, 10)}`
              : ""}
          </>
        }
        actions={
          <>
            <button
              type="button"
              className={s.heroActionBtn}
              onClick={writeToClub}
              disabled={writeBusy}
            >
              {writeBusy ? "Открываем…" : "Написать"}
            </button>
            <a
              href={buildClinicPortalUrl(clinic.subdomain, "/manage")}
              target="_blank"
              rel="noopener noreferrer"
              className={s.heroActionBtnGhost}
            >
              Портал
            </a>
            <button
              type="button"
              className={s.heroActionBtnDanger}
              onClick={deleteClinic}
              disabled={deleteBusy}
            >
              {deleteBusy ? "Отключение…" : "Отключить"}
            </button>
          </>
        }
        compactOnMobile={false}
      />

      {msg ? <div className={s.alertSuccess}>{msg}</div> : null}
      {err ? <div className={s.alertError}>{err}</div> : null}

      <section className={s.metricGrid}>
        <MetricCard label="Тренеры" value={clinic.stats?.trainers ?? 0} />
        <MetricCard label="Клиенты" value={clinic.stats?.clients ?? 0} accent />
        <MetricCard
          label="Абонементы"
          value={clinic.stats?.activeMemberships ?? 0}
        />
        <MetricCard
          label="Выручка 30 дн."
          value={`${formatRub(clinic.stats?.revenue_30d)} ₽`}
        />
      </section>

      {clinic.owner || clinic.activity ? (
        <section className={s.chartPanel}>
          <h3 className={s.panelTitle}>Владелец и активность</h3>
          <div className={s.ownerDetailGrid}>
            <div className={s.infoBlock}>
              <div className={s.metricLabel}>Владелец</div>
              <strong className={s.infoStrong}>
                {clinic.owner?.full_name || "—"}
              </strong>
              <div className={s.clubOwnerLinks}>
                {clinic.owner?.email ? (
                  <a
                    href={`mailto:${clinic.owner.email}`}
                    className={s.clubContactLink}
                  >
                    {clinic.owner.email}
                  </a>
                ) : null}
                {clinic.owner?.phone ? (
                  <a
                    href={`tel:${clinic.owner.phone}`}
                    className={s.clubContactLink}
                  >
                    {clinic.owner.phone}
                  </a>
                ) : null}
              </div>
              {clinic.owner ? (
                <span
                  className={`${s.legalBadge} ${
                    ownerLegalAccepted(clinic.owner)
                      ? s.legalBadgeOk
                      : s.legalBadgeWarn
                  }`}
                >
                  {ownerLegalLabel(clinic.owner)}
                </span>
              ) : null}
            </div>
            <div className={s.infoBlock}>
              <div className={s.metricLabel}>Последняя активность</div>
              <strong className={s.infoStrong}>
                {clinic.activity?.last_activity_at
                  ? new Date(clinic.activity.last_activity_at).toLocaleString(
                      "ru-RU",
                    )
                  : "Нет данных"}
              </strong>
              <p className={s.infoMuted}>
                Вход:{" "}
                {clinic.activity?.last_staff_login_at
                  ? new Date(
                      clinic.activity.last_staff_login_at,
                    ).toLocaleString("ru-RU")
                  : "—"}
                <br />
                Занятие:{" "}
                {clinic.activity?.last_session_at
                  ? new Date(clinic.activity.last_session_at).toLocaleString(
                      "ru-RU",
                    )
                  : "—"}
              </p>
            </div>
          </div>
        </section>
      ) : null}

      <form className={`${s.chartPanel} ${s.billingSetup}`} onSubmit={saveBilling}>
        <div className={s.panelHeaderRow}>
          <h3 className={s.panelTitle}>Подписка</h3>
          {selectedPlan ? (
            <span className={s.planBadgeSoft}>{selectedPlan.name}</span>
          ) : null}
        </div>

        <div className={s.billingGuide}>
          <strong>Три слоя</strong>
          <ol className={s.billingGuideList}>
            <li>
              <b>План</b> — чем пользуется сейчас (фичи: соцсеть, брендинг…).
            </li>
            <li>
              <b>Планы в оплате</b> — что сможет выбрать при продлении.
            </li>
            <li>
              <b>Цена</b> — сколько списывать; фиксация — по желанию.
            </li>
          </ol>
        </div>

        <section className={s.billingStepCard}>
          <div className={s.billingStepHead}>
            <span className={s.billingStepNum}>1</span>
            <div>
              <h4 className={s.billingStepTitle}>Текущий план</h4>
              <p className={s.billingStepHint}>
                От него зависят фичи. Смена лендинга его не трогает.
              </p>
            </div>
          </div>
          <div className={s.formGrid}>
            <label className={s.field}>
              <span>Тариф</span>
              <select
                value={billing.saas_plan_id}
                onChange={(e) =>
                  setBilling({ ...billing, saas_plan_id: e.target.value })
                }
              >
                <option value="">Не выбран</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.monthly_price_rub} ₽/мес
                    {p.is_public ? "" : " · скрыт с сайта"}
                  </option>
                ))}
              </select>
            </label>
            <label className={s.field}>
              <span>Доступ</span>
              <select
                value={billing.status}
                onChange={(e) =>
                  setBilling({ ...billing, status: e.target.value })
                }
              >
                <option value="trial">Пробный период</option>
                <option value="active">Оплачен / активен</option>
                <option value="suspended">Отключён</option>
              </select>
            </label>
            <label className={s.field}>
              <span>Период</span>
              <select
                value={billing.crm_billing_period}
                onChange={(e) =>
                  setBilling({
                    ...billing,
                    crm_billing_period: e.target.value,
                  })
                }
              >
                <option value="monthly">Месяц</option>
                <option value="yearly">Год</option>
              </select>
            </label>
            <label className={s.field}>
              <span>
                {billing.status === "trial" ? "Демо до" : "Оплачено до"}
              </span>
              <input
                type="date"
                value={billing.crm_paid_until}
                onChange={(e) =>
                  setBilling({ ...billing, crm_paid_until: e.target.value })
                }
              />
            </label>
          </div>
        </section>

        <section className={s.billingStepCard}>
          <div className={s.billingStepHead}>
            <span className={s.billingStepNum}>2</span>
            <div>
              <h4 className={s.billingStepTitle}>Планы в оплате</h4>
              <p className={s.billingStepHint}>
                Что увидит при продлении / смене. Текущий план из блока 1 не
                меняется.
              </p>
            </div>
          </div>

          <div className={s.moduleAccessRow}>
            <button
              type="button"
              className={s.moduleAccessBtn}
              onClick={() =>
                setBilling({
                  ...billing,
                  offer_plan_ids: planIdsByCodes(plans, GRANDFATHER_PLAN_CODES),
                })
              }
            >
              Стартовый · Расш. · Макс.
            </button>
            <button
              type="button"
              className={s.moduleAccessBtn}
              onClick={() =>
                setBilling({
                  ...billing,
                  offer_plan_ids: planIdsPublic(plans),
                })
              }
            >
              Как на лендинге
            </button>
            <button
              type="button"
              className={s.moduleAccessBtn}
              onClick={() =>
                setBilling({
                  ...billing,
                  offer_plan_ids: planIdsAll(plans),
                })
              }
            >
              Все
            </button>
            <button
              type="button"
              className={s.moduleAccessBtn}
              onClick={() => setBilling({ ...billing, offer_plan_ids: [] })}
            >
              Снять
            </button>
          </div>

          <div className={s.billingPlanChecks}>
            {plans.map((p) => {
              const id = String(p.id);
              const checked = billing.offer_plan_ids.includes(id);
              const isCurrent = String(billing.saas_plan_id) === id;
              return (
                <label key={p.id} className={s.billingPlanCheck}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...billing.offer_plan_ids, id]
                        : billing.offer_plan_ids.filter((x) => x !== id);
                      setBilling({ ...billing, offer_plan_ids: next });
                    }}
                  />
                  <span>
                    {p.name}
                    {isCurrent ? " · текущий" : ""}
                    <small>
                      {formatRub(p.monthly_price_rub)} ₽/мес
                      {p.is_public ? " · на лендинге" : ""}
                    </small>
                  </span>
                </label>
              );
            })}
          </div>
          {!billing.offer_plan_ids.length ? (
            <p className={s.fieldHint}>
              Ничего не отмечено — при сохранении добавим текущий план, чтобы
              можно было продлить.
            </p>
          ) : (
            <p className={s.fieldHint}>
              Отмечено: {billing.offer_plan_ids.length}
            </p>
          )}

          <label className={s.field}>
            <span>Действует до (необязательно)</span>
            <input
              type="date"
              value={billing.billing_catalog_until}
              onChange={(e) =>
                setBilling({
                  ...billing,
                  billing_catalog_until: e.target.value,
                })
              }
            />
            <small className={s.fieldHint}>
              После даты в оплате останутся планы с лендинга.
            </small>
          </label>
        </section>

        <section className={s.billingStepCard}>
          <div className={s.billingStepHead}>
            <span className={s.billingStepNum}>3</span>
            <div>
              <h4 className={s.billingStepTitle}>Цена продления</h4>
              <p className={s.billingStepHint}>
                Фиксированная цена клуба или прайс из карточки плана.
              </p>
            </div>
          </div>

          <div className={s.formGrid}>
            <label className={s.field}>
              <span>Цена в месяц, ₽</span>
              <input
                type="number"
                min="0"
                value={billing.crm_monthly_price_rub}
                onChange={(e) =>
                  setBilling({
                    ...billing,
                    crm_monthly_price_rub: e.target.value,
                  })
                }
              />
            </label>
            <label className={s.billingOptionRow}>
              <input
                type="checkbox"
                checked={Boolean(billing.crm_price_locked)}
                onChange={(e) =>
                  setBilling({
                    ...billing,
                    crm_price_locked: e.target.checked,
                  })
                }
              />
              <div>
                <strong>Фиксировать эту цену</strong>
                <span>
                  {billing.crm_price_locked
                    ? "Продление спишет сумму из поля слева"
                    : "Выкл — при оплате возьмётся цена из карточки тарифа"}
                </span>
              </div>
            </label>
          </div>

          <div className={s.checkoutStrip}>
            <div>
              <div className={s.metricLabel}>К оплате сейчас</div>
              <strong className={s.checkoutAmount}>
                {checkoutLoading ? (
                  "…"
                ) : checkout?.amount_rub != null ? (
                  <>
                    {Number(checkout.amount_before_partner_discount_rub) >
                    Number(checkout.amount_rub) ? (
                      <s className={s.checkoutAmountOld}>
                        {formatRub(checkout.amount_before_partner_discount_rub)} ₽
                      </s>
                    ) : null}
                    {formatRub(checkout.amount_rub)} ₽
                  </>
                ) : (
                  "—"
                )}
              </strong>
              {checkout?.checkout_type === "change" &&
              checkout.credit_rub > 0 ? (
                <p className={s.infoMuted}>
                  Зачёт неиспользованных дней: −
                  {formatRub(checkout.credit_rub)} ₽
                </p>
              ) : (
                <p className={s.infoMuted}>
                  {billing.crm_billing_period === "yearly"
                    ? "Годовой период"
                    : "Месячный период"}
                </p>
              )}
            </div>
            <div className={s.formActions}>
              <button type="submit" className={s.heroActionBtnGhost}>
                Сохранить
              </button>
              <button
                type="button"
                className={s.heroActionBtn}
                disabled={payBusy || checkoutLoading || !billing.saas_plan_id}
                onClick={payOnline}
              >
                {payBusy ? (
                  "Подготовка…"
                ) : checkout?.amount_rub ? (
                  <>
                    Оплатить{" "}
                    {Number(checkout.amount_before_partner_discount_rub) >
                    Number(checkout.amount_rub) ? (
                      <>
                        <s className={s.checkoutAmountOld}>
                          {formatRub(checkout.amount_before_partner_discount_rub)} ₽
                        </s>{" "}
                      </>
                    ) : null}
                    {formatRub(checkout.amount_rub)} ₽
                  </>
                ) : (
                  "Оплатить онлайн"
                )}
              </button>
            </div>
          </div>
        </section>
      </form>

      <form className={`${s.chartPanel} ${s.billingSetup}`} onSubmit={saveModules}>
        <div className={s.panelHeaderRow}>
          <h3 className={s.panelTitle}>Add-ons</h3>
          <Link to="/admin/modules" className={s.panelLink}>
            Каталог модулей
          </Link>
        </div>
        <div className={s.billingGuide}>
          <strong>Выдача вручную</strong>
          <p>
            Если фича уже в плане клуба — в его кабинете будет «В плане», купить
            нельзя. Здесь можно выдать модуль без оплаты (дата «до»).
          </p>
        </div>
        <div className={s.formGrid}>
          <label className={s.field}>
            <span>Аналитика Pro до</span>
            <input
              type="date"
              value={modulesForm.analytics_pro_paid_until}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  analytics_pro_paid_until: e.target.value,
                })
              }
            />
          </label>
          <label className={s.field}>
            <span>Мессенджеры до</span>
            <input
              type="date"
              value={modulesForm.messengers_paid_until}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  messengers_paid_until: e.target.value,
                })
              }
            />
          </label>
          <label className={s.field}>
            <span>Соцсеть до</span>
            <input
              type="date"
              value={modulesForm.member_social_paid_until}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  member_social_paid_until: e.target.value,
                })
              }
            />
          </label>
          <label className={s.field}>
            <span>Bitrix24 до</span>
            <input
              type="date"
              value={modulesForm.bitrix24_paid_until}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  bitrix24_paid_until: e.target.value,
                })
              }
            />
          </label>
          <label className={s.field}>
            <span>Брендинг до</span>
            <input
              type="date"
              value={modulesForm.white_label_paid_until}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  white_label_paid_until: e.target.value,
                })
              }
            />
          </label>
        </div>
        <div className={s.toggleRows}>
          <label className={s.toggleRow}>
            <span>Сайт-визитка выдана</span>
            <input
              type="checkbox"
              checked={Boolean(modulesForm.website_purchased)}
              onChange={(e) =>
                setModulesForm({
                  ...modulesForm,
                  website_purchased: e.target.checked,
                })
              }
            />
          </label>
        </div>
        <div className={s.formActions}>
          <button type="submit" className={s.heroActionBtn}>
            Сохранить add-ons
          </button>
        </div>
      </form>

      <section className={s.chartPanel}>
        <div className={s.panelHeaderRow}>
          <h3 className={s.panelTitle}>Пользователи</h3>
          <span className={s.panelCount}>{users.length}</span>
        </div>
        {usersLoading ? (
          <p className={s.emptyHint}>Загрузка…</p>
        ) : users.length ? (
          <div className={s.userCards}>
            {users.map((u) => (
              <div key={u.id} className={s.userCard}>
                <div className={s.userCardMain}>
                  <strong>{u.full_name || u.email}</strong>
                  <small>
                    {u.email}
                    {u.phone ? ` · ${u.phone}` : ""}
                  </small>
                  <div className={s.userCardMeta}>
                    <span className={s.statusPill}>{u.role}</span>
                    <span
                      className={`${s.statusPill} ${
                        u.is_blocked ? s.statusSuspended : s.statusActive
                      }`}
                    >
                      {u.is_blocked ? "Заблокирован" : "Активен"}
                    </span>
                    {u.role === "owner" && u.legal_accepted != null ? (
                      <span
                        className={`${s.legalBadge} ${
                          u.legal_accepted ? s.legalBadgeOk : s.legalBadgeWarn
                        }`}
                      >
                        {ownerLegalLabel(u)}
                      </span>
                    ) : null}
                  </div>
                </div>
                {["owner", "admin", "trainer"].includes(u.role) &&
                !u.is_blocked ? (
                  <button
                    type="button"
                    className={s.heroActionBtnGhost}
                    disabled={writeBusy}
                    onClick={() => openSupportChat(u.id)}
                  >
                    Написать
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className={s.emptyHint}>Пользователей нет</p>
        )}
      </section>

      <section className={s.chartPanel}>
        <h3 className={s.panelTitle}>
          Функции тарифа
          {entitlements?.plan?.name ? ` — ${entitlements.plan.name}` : ""}
        </h3>
        <div className={s.featureChips}>
          {Object.entries(features).map(([key, val]) => {
            const on = val === true || (typeof val === "number" && val > 0);
            return (
              <span
                key={key}
                className={`${s.featureChip} ${on ? s.featureChipOn : s.featureChipOff}`}
              >
                {featureNameByKey[key] || key}
                {typeof val === "number" ? `: ${val}` : ""}
              </span>
            );
          })}
        </div>
      </section>
    </div>
  );
}
